import { Box, Typography, useTheme } from '@mui/material'
import { GridColumns } from '@mui/x-data-grid-pro'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { mainEndpoints } from 'src/api/main-component'
import { DynamicTextTypography } from 'src/components/DynamicTextTypography'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'
import InfiniteScrollTable from 'src/components/table/InfiniteScrollTable'
import { ScheduledMaintenanceStatus } from 'src/enums'
import { useUserCtx } from 'src/hooks/context-hooks'
import { MUIThemeColors } from 'src/mui-theme'
import { mainComponentQueryKeys } from 'src/query-keys'
import { ShowDialogProps } from 'src/show-dialog'
import { getFormattedDate, getRecurrenceText, getThemeColor } from 'src/utils'
import { getCageName } from 'src/utils/function'
import NoHistoryLogs from '../../../assets/svg/no-items-common-image.svg'

interface MainComponentPerformHistoryDialogProps extends ShowDialogProps {
  customerId: EntityId
  mainComponentId?: EntityId
  height?: string
  tableRowHeight?: number
  autoHeight?: boolean
  maxVisibleRowCount?: number
  minimalEmptyStatus?: boolean
}

export const MainComponentPerformHistoryDialog: React.FC<MainComponentPerformHistoryDialogProps> = ({
  customerId,
  mainComponentId,
  height = '60vh',
  tableRowHeight,
  autoHeight,
  maxVisibleRowCount,
  minimalEmptyStatus = false,
}) => {
  const { t } = useTranslation(['maintenance', 'main-component'])
  const theme = useTheme()
  const { activePortal } = useUserCtx()

  const STATIC_COLUMNS: GridColumns = [
    {
      field: 'maintenanceName',
      headerName: t('maintenance-name', { ns: 'maintenance' }),
      flex: 1.5,
      renderCell: ({ row }) => row?.maintenanceName ?? 'N/A',
    },
    {
      field: 'location',
      headerName: t('location', { ns: 'main-component' }),
      flex: 2,
      renderCell: ({ row }) => {
        if (
          !row.performedLocalityName &&
          !row.performedPositionName &&
          !row.performedPositionReference &&
          !row.performedMooring
        ) {
          return <Typography>-</Typography>
        }

        const performedLocality = row?.performedLocalityName ?? '-'
        const performedCage = getCageName(row?.performedPositionName, row?.performedPositionReference)
        const performedMooring = row?.performedMooring ?? '-'

        const formattedLocation = `${performedLocality} - ${performedCage} (${performedMooring})`

        return (
          <DynamicTextTypography
            color={
              row.isExpired && !row.isPerformed
                ? getThemeColor(theme, MUIThemeColors.secondaryMain)
                : getThemeColor(theme, MUIThemeColors.black)
            }
            maxLength={30}
            text={formattedLocation}
          />
        )
      },
    },
    {
      field: 'frequency',
      headerName: t('frequency', { ns: 'maintenance' }),
      flex: 1.2,
      renderCell: ({ row }) => {
        const formattedFrequency = getRecurrenceText(row?.repeatType, row?.frequency, row?.weekDays.toString())
        return (
          <DynamicTextTypography
            color={
              row.isExpired && !row.isPerformed
                ? getThemeColor(theme, MUIThemeColors.secondaryMain)
                : getThemeColor(theme, MUIThemeColors.black)
            }
            maxLength={20}
            text={formattedFrequency}
          />
        )
      },
    },
    {
      field: 'performedDate',
      headerName: t('performed-date', { ns: 'maintenance' }),
      flex: 1,
      renderCell: ({ row }) => {
        const formattedDate = getFormattedDate(row?.performedDate) ?? 'N/A'
        return <Typography>{formattedDate}</Typography>
      },
    },
    {
      field: 'status',
      headerName: t('status', { ns: 'common' }),
      flex: 0.75,
      renderCell: ({ row }) => {
        return row.isExpired && !row.isPerformed ? (
          <Typography color={getThemeColor(theme, MUIThemeColors.secondaryMain)}>
            {row.status == ScheduledMaintenanceStatus.skipped
              ? t('skipped', { ns: 'common' })
              : t('abandoned', { ns: 'common' })}
          </Typography>
        ) : (
          <Typography>{t('performed', { ns: 'common' })}</Typography>
        )
      },
    },
    {
      field: 'performedBy',
      headerName: t('performed-by', { ns: 'maintenance' }),
      flex: 1.5,
      renderCell: ({ row }) => {
        if (!row?.firstName && !row?.lastName) {
          return '-'
        }
        return `${row?.firstName || ''} ${row?.lastName || ''}`.trim()
      },
    },
  ]
  const columns: GridColumns = useMemo(() => [...STATIC_COLUMNS], [])

  return (
    <InfiniteScrollTable
      id={`main-component-perform-history-${activePortal}`}
      columns={columns}
      queryUrl={mainEndpoints.getMaintenancePerformHistory(customerId, mainComponentId as EntityId)}
      queryParams={{}}
      generateQueryKey={(page: number, pageSize: number, fields?: string | undefined) =>
        mainComponentQueryKeys.GET_MAINTENANCE_PERFORM_HISTORY(customerId, mainComponentId, { page, pageSize, fields })
      }
      pageSize={10}
      minRowHeight="36px"
      getRowHeight={() => tableRowHeight ?? 45}
      height={height}
      autoHeight={autoHeight}
      maxVisibleRowCount={maxVisibleRowCount}
      bottomPadding="0px"
      noDataComponent={() => (
        <Box height="100%">
          <EmptyStatusCard
            imageWidth="107px"
            image={NoHistoryLogs}
            header={t('maintenance-logs-empty status-title', { ns: 'activity-logs' })}
            content={t('maintenance-logs-empty status-content', { ns: 'activity-logs' })}
            headerFontSize="18px"
            contentFontSize="14px"
            contentClasses="mt-2"
            contentTopMargin="10px"
            imageContainerClasses="pb-4 pt-0"
            height="100%"
            imageTopMargin="0px"
            hideImage={minimalEmptyStatus}
            compact={minimalEmptyStatus}
          />
        </Box>
      )}
      spinnerHeight={100}
    />
  )
}
